import Box from '@mui/material/Box';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import * as React from 'react';
import { useEffect, useState } from 'react';

interface PossiblePermissionProps {
  label: string;
  value: string;
  children?: PossiblePermissionProps[];
}

const mainPossiblePermissions: PossiblePermissionProps[] = [
  {
    value: 'ALL',
    label: 'Todas as permissões',
    children: [
      { value: 'ALARM_CENTRAL', label: 'Central de Alarme', children: [] },
      { value: 'CONTACT_ID_EVENT', label: 'Evento Contact ID', children: [] },
      { value: 'RESPONSIBLE', label: 'Responsáveis', children: [] },
      { value: 'STUDENT', label: 'Estudantes', children: [] },
      { value: 'USER', label: 'Usuários', children: [] },
      { value: 'SCHOOL_MONITORING_EVENT', label: 'Monitoramento Escolar' },
      { value: 'COMMUNICATION', label: 'Comunicação' },
      { value: 'SIP_CLIENT', label: 'Fiscal Remoto' },
      { value: 'DASHBOARD', label: 'Dashboard' },
      { value: 'FACE_REGISTER', label: 'Gerenciamento de Faces' },
      {
        value: 'EVENTS',
        label: 'Eventos',
        children: [
          {
            value: 'FACE_DETECT_EVENT',
            label: 'Eventos de detecção facial',
            children: [],
          },
          {
            value: 'FACE_RECOGNITION_EVENT',
            label: 'Eventos de reconhecimento facial',
            children: [],
          },
          { value: 'IVS_EVENT', label: 'Eventos IVS', children: [] },
          { value: 'LPR_EVENT', label: 'Eventos LPR', children: [] },
          {
            value: 'RESPONSIBLE_RECOGNITION_EVENT',
            label: 'Eventos de reconhecimento de responsáveis',
            children: [],
          },
          {
            value: 'STUDENT_RECOGNITION_EVENT',
            label: 'Eventos de reconhecimento de estudantes',
            children: [],
          },
          {
            value: 'TEMPERATURE_DETECT_EVENT',
            label: 'Eventos de detecção de temperatura',
            children: [],
          },
        ],
      },
      {
        value: 'DEVICES',
        label: 'Dispositivos',
        children: [
          {
            value: 'FACE_RECOGNITION_DEVICE',
            label: 'Câmeras de reconhecimento facial',
            children: [],
          },
          {
            value: 'FACIAL_CONTROL_DEVICE',
            label: 'Controladores faciais',
            children: [],
          },
          {
            value: 'IVS_DEVICE',
            label: 'Câmeras IVS',
            children: [],
          },
          {
            value: 'LPR_DEVICE',
            label: 'Câmeras LPR',
            children: [],
          },
          {
            value: 'TEMPERATURE_DETECTOR_DEVICE',
            label: 'Câmeras de detecção de temperatura',
            children: [],
          },
          {
            value: 'TUYA_DEVICE',
            label: 'Dispositivos Tuya',
            children: [],
          },
          {
            value: 'RECOGNITION_GROUP',
            label: 'Grupos de Reconhecimento',
            children: [],
          },
          {
            value: 'RECOGNITION',
            label: 'Reconhecimento',
            children: [
              {
                value: 'GENERAL_RECOGNITION',
                label: 'Geral',
                children: [],
              },
              {
                value: 'CUSTOMER_RECOGNITION',
                label: 'Cliente',
                children: [],
              },
            ],
          },
        ],
      },
      {
        value: 'INTEGRATIONS',
        label: 'Integrações',
        children: [
          {
            value: 'CIGAM_INTEGRATION',
            label: 'Cigam',
            children: [],
          },
          {
            value: 'DEFENSE_INTEGRATION',
            label: 'Defense',
            children: [],
          },
          {
            value: 'MONI_INTEGRATION',
            label: 'Moni',
            children: [],
          },
          {
            value: 'SIGMA_INTEGRATION',
            label: 'Sigma',
            children: [],
          },
          {
            value: 'SURGARD_INTEGRATION',
            label: 'Surgard',
            children: [],
          },
          {
            value: 'TUYA_INTEGRATION',
            label: 'Tuya',
            children: [],
          },
          {
            value: 'WEBHOOK_INTEGRATION',
            label: 'Webhook',
            children: [],
          },
        ],
      },
    ],
  },
];

interface PermissionsMultiselectProps {
  onChange: (value: string[]) => void;
  value: string[];
}

const PermissionsMultiselect = ({
  value,
  onChange,
}: PermissionsMultiselectProps) => {
  const [permissions, setPermissions] = useState<PossiblePermissionProps[]>([]);
  const [expandedItems, setExpandedItems] = useState<string[]>(['ALL']);
  const createTreePermissions = () => {
    const newTree: PossiblePermissionProps[] = [...mainPossiblePermissions];
    mount(mainPossiblePermissions, newTree);

    setPermissions(newTree);
  };

  const mount = (
    mainPossibles: PossiblePermissionProps[],
    newTree: PossiblePermissionProps[],
  ) => {
    mainPossibles.forEach(({ value, children }) => {
      if (children && children.length > 0) {
        mount(children, newTree);
        return;
      }
      const possibles: PossiblePermissionProps[] = [
        { label: 'Criar', value: `${value}_CREATE`, children: [] },
        { label: 'Ler', value: `${value}_READ`, children: [] },
        { label: 'Atualizar', value: `${value}_UPDATE`, children: [] },
        { label: 'Excluir', value: `${value}_DELETE`, children: [] },
      ];
      if (children) children.push(...possibles);
    });
  };

  useEffect(() => {
    createTreePermissions();
  }, []);

  const renderTreeItem = (permission: PossiblePermissionProps[]) => {
    return permission.map((p) => {
      return (
        <TreeItem itemId={p.value} key={p.value} label={p.label}>
          {p.children && p.children.length > 0 && renderTreeItem(p.children)}
        </TreeItem>
      );
    });
  };

  return (
    <Box sx={{ minHeight: 352, minWidth: 290, mb: 2 }}>
      <SimpleTreeView
        checkboxSelection
        multiSelect
        expandedItems={expandedItems}
        onExpandedItemsChange={(
          _: any,
          itemIds: React.SetStateAction<string[]>,
        ) => setExpandedItems(itemIds)}
        onSelectedItemsChange={(_: any, itemIds: string[]) => onChange(itemIds)}
        selectedItems={value}
      >
        {renderTreeItem(permissions)}
      </SimpleTreeView>
    </Box>
  );
};

export default PermissionsMultiselect;
